/**
 * sl-ya4v.15: useBrandedSubdomainAvailability — non-suspense lookup of the
 * rename-rate-limit gate (`canRename` + `nextRenameAt` + `currentLabel`).
 *
 * The settings panel calls this BEFORE opening the rename modal so it can:
 *   - disable the rename CTA when the 30-day window is still active,
 *   - surface the precise `nextRenameAt` timestamp inline,
 *   - prefill the modal with the current label so the user sees old → new.
 *
 * Mirrors `useWorkspacePublicHost` (sl-ya4v.6): uses `useSWRImmutable` with
 * `{ suspense: false }` so the panel keeps rendering while the query settles.
 */
import { GraphQLService } from '@affine/core/modules/cloud';
import {
  type BrandedSubdomainRenameAvailabilityQuery,
  brandedSubdomainRenameAvailabilityQuery,
} from '@affine/graphql';
import { useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import useSWRImmutable from 'swr/immutable';

export interface UseBrandedSubdomainAvailabilityResult {
  /** True iff workspace has a branded subdomain AND the 30-day window has elapsed. */
  canRename: boolean;
  /** Earliest UTC instant the next rename will be accepted. Null if never renamed. */
  nextRenameAt: string | null;
  /** Current bare label (no apex). Null when no branded subdomain registered. */
  currentLabel: string | null;
  isLoading: boolean;
  error: GraphQLError | undefined;
  refresh: () => Promise<unknown>;
}

type AvailabilitySnapshot =
  BrandedSubdomainRenameAvailabilityQuery['brandedSubdomainRenameAvailability'];

export function useBrandedSubdomainAvailability(
  workspaceId: string | null
): UseBrandedSubdomainAvailabilityResult {
  const graphql = useService(GraphQLService);

  const { data, isLoading, error, mutate } = useSWRImmutable<
    AvailabilitySnapshot | null,
    GraphQLError
  >(
    workspaceId ? ['brandedSubdomainRenameAvailability', workspaceId] : null,
    async () => {
      const res = await graphql.gql({
        query: brandedSubdomainRenameAvailabilityQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return res.brandedSubdomainRenameAvailability ?? null;
    },
    { suspense: false }
  );

  return {
    canRename: data?.canRename ?? false,
    nextRenameAt: data?.nextRenameAt ?? null,
    currentLabel: data?.currentLabel ?? null,
    isLoading,
    error,
    refresh: () => mutate(),
  };
}
